import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  BookOpen,
  Play,
  Clock,
  Calendar,
  User,
  GraduationCap,
  Video,
  FileText,
  Lightbulb,
  Target,
  ChevronRight,
  ExternalLink,
  ArrowRight,
  Star,
  TrendingUp,
} from "lucide-react";
import { AffiliateHeaderContext } from "../../context/AffiliateHeaderContext";
import {
  getPublishedEducationContent,
  getYouTubeVideoId,
  getYouTubeEmbedUrl,
} from "../../lib/educationService";
import Card from "../ui/Card";
import Button from "../ui/Button";
import Alert from "../ui/Alert";

const EducationPage = () => {
  const { affiliateName } = useParams();
  const navigate = useNavigate();
  const decodedAffiliateName = decodeURIComponent(affiliateName);
  const setHeaderContent = useContext(AffiliateHeaderContext);

  const [educationContent, setEducationContent] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Set header content
  useEffect(() => {
    setHeaderContent(
      <div className="flex items-center space-x-4">
        <div className="w-2 h-8 bg-gradient-to-b from-[#B8460E] to-[#8B3A0E] rounded-full"></div>
        <div>
          <h1 className="text-2xl font-bold text-white">Education Center</h1>
          <p className="text-gray-400 text-sm">
            Master the art of high-converting affiliate marketing
          </p>
        </div>
      </div>
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  // Load education content
  useEffect(() => {
    const loadEducationContent = async () => {
      try {
        setIsLoading(true);
        setError(null);

        console.log("🚀 EDUCATION-PAGE: Loading education content");

        const content = await getPublishedEducationContent();

        console.log(
          `✅ EDUCATION-PAGE: Loaded ${content.length} education items`
        );

        setEducationContent(content);
      } catch (err) {
        console.error("❌ EDUCATION-PAGE: Error loading content:", err);
        setError("Failed to load education content. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    loadEducationContent();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#B8460E] mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading education content...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert type="error">
        <p className="font-semibold">Error Loading Education Content</p>
        <p>{error}</p>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </Alert>
    );
  }

  // Empty state
  if (educationContent.length === 0) {
    return (
      <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-12 text-center">
        <div className="w-16 h-16 bg-[#B8460E]/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <GraduationCap className="w-8 h-8 text-[#B8460E]" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">
          No Education Content Available
        </h3>
        <p className="text-gray-400">
          Check back later for new training materials and courses.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="p-6 text-center">
          <div className="w-12 h-12 bg-[#B8460E]/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <GraduationCap className="w-6 h-6 text-[#B8460E]" />
          </div>
          <h3 className="text-2xl font-bold text-white">
            {educationContent.length}
          </h3>
          <p className="text-gray-400 text-sm">Total Courses</p>
        </Card>
        <Card className="p-6 text-center">
          <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <Video className="w-6 h-6 text-blue-400" />
          </div>
          <h3 className="text-2xl font-bold text-white">
            {educationContent.filter((c) => c.videoUrl).length}
          </h3>
          <p className="text-gray-400 text-sm">Video Courses</p>
        </Card>
        <Card className="p-6 text-center">
          <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <TrendingUp className="w-6 h-6 text-green-400" />
          </div>
          <h3 className="text-2xl font-bold text-white">
            {educationContent.filter((c) => !c.videoUrl).length}
          </h3>
          <p className="text-gray-400 text-sm">Written Guides</p>
        </Card>
      </div>

      {/* Course Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {educationContent.map((content, index) => (
          <div
            key={content.id}
            className="relative overflow-hidden group cursor-pointer transition-all duration-300 transform hover:scale-[1.02] hover:shadow-2xl bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-0"
            onClick={() => {
              navigate(`/affiliate/${affiliateName}/education/${content.id}`);
            }}
          >
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div
                className="absolute w-32 h-32 bg-[#B8460E]/10 rounded-full blur-2xl animate-pulse"
                style={{ animationDelay: `${index * 0.5}s` }}
              ></div>
            </div>

            {/* Shine effect on hover */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 pointer-events-none"></div>

            <div className="relative z-10 p-6 h-full">
              {/* Thumbnail or placeholder */}
              <div className="relative mb-4">
                {content.thumbnailUrl ? (
                  <div className="w-full h-48 bg-black rounded-xl overflow-hidden relative">
                    <img
                      src={content.thumbnailUrl}
                      alt={content.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {/* Overlay with content type */}
                    <div className="absolute top-3 left-3">
                      {content.videoUrl ? (
                        <div className="flex items-center gap-1 px-2 py-1 bg-[#B8460E]/90 backdrop-blur-sm text-white rounded-full text-xs font-medium">
                          <Video className="h-3 w-3" />
                          <span>Video</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 px-2 py-1 bg-blue-500/90 backdrop-blur-sm text-white rounded-full text-xs font-medium">
                          <FileText className="h-3 w-3" />
                          <span>Article</span>
                        </div>
                      )}
                    </div>
                    {/* Play button overlay for videos */}
                    {content.videoUrl && (
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-16 h-16 bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center">
                          <Play className="h-8 w-8 text-white ml-1" />
                        </div>
                      </div>
                    )}
                  </div>
                ) : content.videoUrl ? (
                  <div className="w-full h-48 bg-gradient-to-br from-[#B8460E]/20 to-[#8B3A0E]/20 rounded-xl flex items-center justify-center relative overflow-hidden">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#B8460E]/30 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Play className="h-8 w-8 text-[#B8460E] ml-1" />
                      </div>
                      <p className="text-[#B8460E] text-sm font-medium">
                        Video Course
                      </p>
                    </div>
                    <div className="absolute top-3 left-3">
                      <div className="flex items-center gap-1 px-2 py-1 bg-[#B8460E]/90 backdrop-blur-sm text-white rounded-full text-xs font-medium">
                        <Video className="h-3 w-3" />
                        <span>Video</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-48 bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-xl flex items-center justify-center relative overflow-hidden">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-blue-500/30 rounded-full flex items-center justify-center mx-auto mb-3">
                        <BookOpen className="h-8 w-8 text-blue-400" />
                      </div>
                      <p className="text-blue-400 text-sm font-medium">
                        Written Guide
                      </p>
                    </div>
                    <div className="absolute top-3 left-3">
                      <div className="flex items-center gap-1 px-2 py-1 bg-blue-500/90 backdrop-blur-sm text-white rounded-full text-xs font-medium">
                        <FileText className="h-3 w-3" />
                        <span>Article</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Content info */}
              <h3 className="text-xl font-bold text-white mb-3 line-clamp-2 group-hover:text-[#B8460E] transition-colors">
                {content.title}
              </h3>

              {content.description && (
                <p className="text-gray-400 text-sm mb-4 line-clamp-3 leading-relaxed">
                  {content.description}
                </p>
              )}

              {/* Meta info */}
              <div className="flex items-center justify-between text-sm text-gray-400 mt-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{formatDate(content.createdAt)}</span>
                </div>
                <div className="flex items-center gap-2 text-[#B8460E] group-hover:translate-x-1 transition-transform">
                  <span className="text-sm font-medium">Learn More</span>
                  <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Call to Action */}
      <Card className="p-8 text-center bg-gradient-to-br from-[#B8460E]/10 to-[#8B3A0E]/10 border-[#B8460E]/20">
        <div className="max-w-2xl mx-auto">
          <div className="w-16 h-16 bg-[#B8460E]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Target className="w-8 h-8 text-[#B8460E]" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-3">
            Ready to Boost Your Performance?
          </h3>
          <p className="text-gray-300 mb-6">
            Apply the strategies from these courses to maximize your affiliate
            success and increase your commissions.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Button
              onClick={() => navigate(`/affiliate/${affiliateName}`)}
              className="bg-[#B8460E] hover:bg-[#8B3A0E] text-white"
            >
              <TrendingUp className="h-4 w-4 mr-2" />
              View Performance
            </Button>
            <Button
              onClick={() =>
                navigate(`/affiliate/${affiliateName}/referral-center`)
              }
              className="bg-white/10 hover:bg-white/20 text-white border-white/20"
            >
              <Star className="h-4 w-4 mr-2" />
              Refer Others
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default EducationPage;
